import React, {useEffect, useMemo, useState} from "react";
import {BusinessDealInterface} from "../../../service/Business/Interfaces/BusinessDealInterface";
import StatusBusinessDeal from "../../../utils/Business/StatusBusinessDeal";
import FCDatePicker from "../../FCObjets/Form/FCDatePicker";
import {AddBusiness, UpdateBusiness} from "../../../service/Business/BusinessService";
import {useFetchCustomers} from "../../../service/Business/CustomerService";
import {getIdWithToken} from "../../../utils/storage/Token";
import FCSpinner from "../../FCObjets/FCSpinner";
import {CallbackErrorModalFL, CallbackFinishedModalFL} from "../../Dashboard/Form/ModalForm";
import FCAlertMsgErrorAPI from "../../FCObjets/FCAlertMsg/FCAlertMsgErrorAPI";
import {useFetchConfigUser} from "../../../service/AccountService";
import FCSelect, {FCSelectOption} from "../../FCObjets/Form/FCSelect";
import formatEuro from "../../../utils/formatEuro";
import {useToast} from "../../FCObjets/FCToast/FCToast";
import "./FormAtelier.scss";

type FormBusinessProps = {
    onFinished: CallbackFinishedModalFL;
    onError: CallbackErrorModalFL;
    dataBusiness: BusinessDealInterface | undefined;
};

const FormBusinessDeal: React.FunctionComponent<FormBusinessProps> = ({dataBusiness, onFinished, onError}) => {
    const toast = useToast();
    const {data: configUser} = useFetchConfigUser(getIdWithToken());
    const {data: customers, loading, error} = useFetchCustomers(getIdWithToken());

    const [inputCustomer, setInputCustomer] = useState<number>(dataBusiness ? dataBusiness.customer.id : -1);
    const [inputName, setInputName] = useState(dataBusiness ? dataBusiness.name : "");
    const [inputDescription, setInputDescription] = useState(dataBusiness ? dataBusiness.description : "");
    const [inputStatus, setInputStatus] = useState<number>(dataBusiness ? dataBusiness.state : 0);
    const [inputTJM, setInputTJM] = useState<number>(dataBusiness ? dataBusiness.details.tjm : 0);
    const [inputDayExpected, setInputDayExpected] = useState<number>(
        dataBusiness ? dataBusiness.details.totalOfDaysExpected : 0,
    );
    const [dateBegin, setDateBegin] = useState<Date | null>(dataBusiness ? new Date(dataBusiness.details.begin) : null);
    const [dateEnd, setDateEnd] = useState<Date | null>(dataBusiness ? new Date(dataBusiness.details.end) : null);
    const [inputNumber, setInputNumber] = useState(dataBusiness ? dataBusiness.details.number : "");
    const [inputNumAffaire, setInputNumAffaire] = useState(dataBusiness ? dataBusiness.details.numAffaire : "");
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    // Chargement du TJM par défaut défini dans les settings utilisateur.
    useEffect(() => {
        if (configUser && !dataBusiness) {
            setInputTJM(configUser.user.userInfo.tjmDefault);
        }
    }, [configUser, dataBusiness]);

    const totalEstime = useMemo(() => (inputTJM || 0) * (inputDayExpected || 0), [inputTJM, inputDayExpected]);


    /**
     * Validation du formulaire
     */
    const handleSubmit = () => {
        const errors: Record<string, string> = {};

        if (!inputName.trim()) errors.inputName = "Le nom du contrat est obligatoire.";
        if (!inputNumber.trim()) errors.inputNumber = "Le numéro de contrat est obligatoire.";
        if (inputCustomer === -1 && (!customers || customers.length === 0)) errors.inputCustomer = "Aucun client disponible. Créez un client d'abord.";
        if (inputTJM <= 0) errors.inputTJM = "Le TJM doit être supérieur à 0.";
        if (inputDayExpected <= 0) errors.inputDayExpected = "Le nombre de jours doit être supérieur à 0.";
        if (!dateBegin) errors.dateBegin = "La date de début est obligatoire.";
        if (!dateEnd) errors.dateEnd = "La date de fin est obligatoire.";
        if (dateBegin && dateEnd && dateEnd < dateBegin) errors.dateEnd = "La date de fin doit être postérieure à la date de début.";

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            toast.error(Object.values(errors).join(" "), "Validation");
            return;
        }
        setFieldErrors({});

        let customer = dataBusiness?.customer;
        if (customers) {
            customer = customers.find((c: { id: number }) => c.id === inputCustomer);
        }
        if (inputCustomer === -1 && customers && customers.length > 0) {
            customer = customers[0];
        }

        const business: any = {
            customer: customer,
            description: inputDescription,
            details: {
                id: dataBusiness ? dataBusiness.details.id : -1,
                tjm: inputTJM,
                begin: dateBegin?.toDateString(),
                end: dateEnd?.toDateString(),
                totalOfDaysExpected: inputDayExpected,
                number: inputNumber,
                numAffaire: inputNumAffaire,
            },
            id: dataBusiness ? dataBusiness.id : -1,
            name: inputName,
            state: inputStatus,
        };

        const promise = dataBusiness
            ? UpdateBusiness(dataBusiness.id, business)
            : AddBusiness(business);

        promise.then(() => {
            toast.success(dataBusiness ? "Contrat modifié avec succès." : "Contrat ajouté avec succès.", "Contrat");
            onFinished();
        }).catch((err) => onError(err));
    };

    return (
        <form className="fcaForm" onSubmit={(e) => e.preventDefault()}>
            {loading && <FCSpinner size="small"/>}
            {error && <FCAlertMsgErrorAPI error={error}/>}

            {/* Section 1 — Assignation */}
            <div className="fcaSection">
                <div className="fcaSectionLabel">Assignation</div>
                <div className="fcaRow">
                    <div className={`fcaField${fieldErrors.inputCustomer ? " fcaField--error" : ""}`}>
                        <label>Client</label>
                        <FCSelect
                            options={[
                                {value: -1, label: "— Sélectionner un client —"},
                                ...(customers ? customers.map((c: {id: number; companyName: string}) => ({value: c.id, label: c.companyName})) : [])
                            ] as FCSelectOption[]}
                            value={inputCustomer}
                            onChange={(v) => setInputCustomer(Number(v))}
                            placeholder="— Sélectionner un client —"
                        />
                        {fieldErrors.inputCustomer && <span className="fcaFieldError">{fieldErrors.inputCustomer}</span>}
                    </div>
                    <div className={`fcaField${fieldErrors.inputNumber ? " fcaField--error" : ""}`}>
                        <label htmlFor="num">Numéro de contrat *</label>
                        <input id="num" type="text" value={inputNumber}
                               onChange={(e) => setInputNumber(e.target.value)}/>
                        {fieldErrors.inputNumber && <span className="fcaFieldError">{fieldErrors.inputNumber}</span>}
                    </div>
                </div>
            </div>

            {/* Section 2 — Identité */}
            <div className="fcaSection">
                <div className="fcaSectionLabel">Identité du contrat</div>
                <div className={`fcaField${fieldErrors.inputName ? " fcaField--error" : ""}`}>
                    <label htmlFor="nom">Nom du contrat</label>
                    <input id="nom" type="text" value={inputName}
                           onChange={(e) => setInputName(e.target.value)}/>
                    {fieldErrors.inputName && <span className="fcaFieldError">{fieldErrors.inputName}</span>}
                </div>
                <div className="fcaField">
                    <label htmlFor="numAff">Numéro d'affaire</label>
                    <input id="numAff" type="text" value={inputNumAffaire}
                           onChange={(e) => setInputNumAffaire(e.target.value)}/>
                </div>
            </div>

            {/* Section 3 — Statut */}
            <div className="fcaSection">
                <div className="fcaSectionLabel">Statut</div>
                <div className="fcaTabs">
                    {StatusBusinessDeal.ListStatus.map((s) => (
                        <button type="button" key={s}
                                className={`fcaTab${inputStatus === s ? " active" : ""}`}
                                onClick={() => setInputStatus(s)}>
                            {StatusBusinessDeal.toString(s)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Section 4 — Cadre financier */}
            <div className="fcaSection">
                <div className="fcaSectionLabel">Cadre financier</div>
                <div className="fcaRow">
                    <div className={`fcaField${fieldErrors.inputTJM ? " fcaField--error" : ""}`}>
                        <label htmlFor="tjm">TJM (€)</label>
                        <input id="tjm" type="number" value={inputTJM}
                               onChange={(e) => setInputTJM(parseInt(e.target.value) || 0)}/>
                        {fieldErrors.inputTJM && <span className="fcaFieldError">{fieldErrors.inputTJM}</span>}
                    </div>
                    <div className={`fcaField${fieldErrors.inputDayExpected ? " fcaField--error" : ""}`}>
                        <label htmlFor="numberDays">Nombre de jours</label>
                        <input id="numberDays" type="number" value={inputDayExpected}
                               onChange={(e) => setInputDayExpected(parseInt(e.target.value) || 0)}/>
                        {fieldErrors.inputDayExpected && <span className="fcaFieldError">{fieldErrors.inputDayExpected}</span>}
                    </div>
                </div>
                <div className="fcaHighlight">
                    <div className="fcaHighlightLabel">Total estimé</div>
                    <div className="fcaHighlightValue">{formatEuro(totalEstime, 0)}</div>
                </div>
            </div>

            {/* Section 5 — Période */}
            <div className="fcaSection">
                <div className="fcaSectionLabel">Période</div>
                <div className="fcaRow">
                    <div className={`fcaField${fieldErrors.dateBegin ? " fcaField--error" : ""}`}>
                        <FCDatePicker onChangeHandler={setDateBegin} value={dateBegin} id="deb" label="Date de début"/>
                        {fieldErrors.dateBegin && <span className="fcaFieldError">{fieldErrors.dateBegin}</span>}
                    </div>
                    <div className={`fcaField${fieldErrors.dateEnd ? " fcaField--error" : ""}`}>
                        <FCDatePicker onChangeHandler={setDateEnd} value={dateEnd} id="fin" label="Date de fin"/>
                        {fieldErrors.dateEnd && <span className="fcaFieldError">{fieldErrors.dateEnd}</span>}
                    </div>
                </div>
            </div>

            {/* Section 6 — Portée */}
            <div className="fcaSection">
                <div className="fcaSectionLabel">Portée de l'accord</div>
                <div className="fcaField">
                    <textarea value={inputDescription} placeholder="Description des prestations, livrables, conditions…"
                              onChange={(e) => setInputDescription(e.target.value)}/>
                </div>
            </div>

            <div className="fcaActions">
                <button type="button" className="fcaBtn fcaBtnSecondary" onClick={() => onFinished()}>
                    Annuler
                </button>
                <button type="button" className="fcaBtn fcaBtnPrimary" onClick={handleSubmit}>
                    <span className="material-symbols-outlined">check</span>
                    {dataBusiness ? "Enregistrer" : "Créer le contrat"}
                </button>
            </div>
        </form>
    );
};

export default FormBusinessDeal;
